import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  AccountKindValue,
  accountKindRefusal,
  isAccountKind,
} from '@internal/datasphere-personas';
import { UserEmailSearchField } from '../UserEmailSearchField';

/** `ui:options` of the field. */
export interface OwnerEmailPickerOptions {
  /** The account kind the field needs: `normal` or `admin`. */
  accountKind?: AccountKindValue;
}

type UiSchema = { 'ui:options'?: unknown } | undefined;

const accountKindOption = (
  uiSchema: UiSchema,
): AccountKindValue | undefined => {
  const options = uiSchema?.['ui:options'] as
    | OwnerEmailPickerOptions
    | undefined;
  return isAccountKind(options?.accountKind) ? options.accountKind : undefined;
};

/** Error messages for a field value; empty when the value is valid. */
export const ownerEmailErrors = (
  email: string | undefined,
  uiSchema: UiSchema,
  role: string,
): string[] => {
  const kind = accountKindOption(uiSchema);
  const refusal =
    email && kind ? accountKindRefusal({ email }, kind, role) : undefined;
  return refusal ? [refusal] : [];
};

/**
 * Field validation. The field's title names the role in the message, so the
 * Spoke Owner and the Acceptance Owner get their own wording.
 */
export const validateOwnerEmail = (
  email: string | undefined,
  validation: { addError: (message: string) => void },
  context: { schema?: { title?: unknown }; uiSchema?: UiSchema },
): void => {
  const role =
    typeof context.schema?.title === 'string' ? context.schema.title : 'Owner';
  for (const error of ownerEmailErrors(email, context.uiSchema, role)) {
    validation.addError(error);
  }
};

/**
 * Autocomplete field for the Hub/Spoke "Owner" that searches the Azure
 * user-lookup backend route as the user types, then lets them select a result.
 *
 * The search is served in-process by the Backstage backend azure plugin.
 */
export const OwnerEmailPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  uiSchema,
}: FieldExtensionComponentProps<string>) => {
  return (
    <UserEmailSearchField
      value={formData}
      onChange={onChange}
      label={schema?.title ?? 'Owner'}
      required={required}
      rawErrors={rawErrors}
      helperText={
        (uiSchema?.['ui:help'] as string | undefined) ?? schema?.description
      }
      margin="normal"
      accountKind={accountKindOption(uiSchema)}
    />
  );
};
